import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import {
  AlertCircle,
  CheckCircle,
  Download,
  FileSpreadsheet,
  RefreshCw,
  Trash2,
  Upload,
  XCircle,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import { barrattAPI, type ImportSummary, type ImportTemplate, type ImportException } from '../lib/api/barratt';
import { useConfirm } from '../contexts/ConfirmDialogContext';
import { format } from 'date-fns';

const statusColors: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-800',
  processing: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  completed_with_errors: 'bg-yellow-100 text-yellow-800',
  failed: 'bg-red-100 text-red-800',
};

const statusIcons: Record<string, React.ReactNode> = {
  pending: <Clock className="h-4 w-4" />,
  processing: <RefreshCw className="h-4 w-4 animate-spin" />,
  completed: <CheckCircle className="h-4 w-4" />,
  completed_with_errors: <AlertTriangle className="h-4 w-4" />,
  failed: <XCircle className="h-4 w-4" />,
};

const BulkUpload: React.FC = () => {
  const confirm = useConfirm();
  const [imports, setImports] = useState<ImportSummary[]>([]);
  const [templates, setTemplates] = useState<ImportTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [exceptionsDialogOpen, setExceptionsDialogOpen] = useState(false);
  const [selectedImport, setSelectedImport] = useState<ImportSummary | null>(null);
  const [exceptions, setExceptions] = useState<ImportException[]>([]);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadType, setUploadType] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [importsRes, templatesRes] = await Promise.all([
        barrattAPI.getImportSummaries(),
        barrattAPI.getImportTemplates(),
      ]);

      setImports(importsRes.data.results || []);
      setTemplates(templatesRes.data.templates || []);
    } catch (error) {
      console.error('Failed to load import data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadType) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('template_id', uploadType);

      await barrattAPI.uploadFile(formData);
      setUploadDialogOpen(false);
      setUploadFile(null);
      setUploadType('');
      loadData();
    } catch (error) {
      console.error('Failed to upload file:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleViewExceptions = async (importSummary: ImportSummary) => {
    setSelectedImport(importSummary);
    try {
      const res = await barrattAPI.getImportSummaryExceptions(importSummary.id);
      setExceptions(res.data || []);
      setExceptionsDialogOpen(true);
    } catch (error) {
      console.error('Failed to load exceptions:', error);
    }
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({ title: 'Delete Import', description: 'Are you sure you want to delete this import record?', confirmLabel: 'Delete', variant: 'destructive' });
    if (!ok) return;
    try {
      await barrattAPI.deleteImportSummary(id);
      loadData();
    } catch (error) {
      console.error('Failed to delete import record:', error);
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setUploadFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploadFile(e.target.files[0]);
    }
  };

  const downloadTemplate = (template: ImportTemplate) => {
    // Create CSV content
    const csvContent = template.columns.join(',') + '\n';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template.name.replace(/\s+/g, '_')}_template.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Bulk Data Upload</h1>
          <p className="text-gray-500">Import data from CSV files</p>
        </div>
        <Button onClick={() => setUploadDialogOpen(true)}>
          <Upload className="h-4 w-4 mr-2" />
          Upload File
        </Button>
      </div>

      {/* Templates */}
      <Card>
        <CardHeader>
          <CardTitle>Import Templates</CardTitle>
          <CardDescription>
            Download CSV templates for different import types
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {templates.map((template) => (
              <Card
                key={template.id}
                className="cursor-pointer hover:border-blue-300 transition-colors"
                onClick={() => downloadTemplate(template)}
              >
                <CardContent className="pt-4 text-center">
                  <FileSpreadsheet className="h-8 w-8 mx-auto mb-2 text-green-600" />
                  <p className="font-medium text-sm">{template.name}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {template.columns.length} columns
                  </p>
                  <Button variant="ghost" size="sm" className="mt-2">
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Import History */}
      <Card>
        <CardHeader>
          <CardTitle>Import History</CardTitle>
          <CardDescription>
            {imports.length} imports found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : imports.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No imports yet. Upload a file to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Import #</TableHead>
                  <TableHead>Dataset</TableHead>
                  <TableHead className="text-center">Records</TableHead>
                  <TableHead className="text-center">Pages</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {imports.map((importItem) => {
                  const status = importItem.is_complete
                    ? (importItem.is_success ? 'completed' : 'completed_with_errors')
                    : 'processing';
                  const statusDisplay = importItem.is_complete
                    ? (importItem.is_success ? 'Completed' : 'Completed with errors')
                    : 'Processing';
                  return (
                    <TableRow key={importItem.id}>
                      <TableCell className="font-medium">
                        #{importItem.id}
                      </TableCell>
                      <TableCell>{importItem.dataset_name || 'N/A'}</TableCell>
                      <TableCell className="text-center">{importItem.total_records}</TableCell>
                      <TableCell className="text-center">
                        {importItem.imported_pages}/{importItem.total_pages}
                      </TableCell>
                      <TableCell>
                        <Progress
                          value={(importItem.imported_pages / Math.max(importItem.total_pages, 1)) * 100}
                          className="w-20 h-2"
                        />
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[status]}>
                          <span className="flex items-center gap-1">
                            {statusIcons[status]}
                            {statusDisplay}
                          </span>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {format(new Date(importItem.created_at), 'dd MMM yyyy HH:mm')}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewExceptions(importItem)}
                          >
                            <AlertCircle className="h-4 w-4 mr-1" />
                            Details
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(importItem.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Import File</DialogTitle>
            <DialogDescription>
              Select an import type and upload your CSV file
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Import Type *</label>
              <Select value={uploadType} onValueChange={setUploadType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select import type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="purchase_orders">Purchase Orders</SelectItem>
                  <SelectItem value="deliveries">Deliveries/GRNs</SelectItem>
                  <SelectItem value="suppliers">Suppliers</SelectItem>
                  <SelectItem value="spend_data">Spend Data</SelectItem>
                  <SelectItem value="prices">Price List</SelectItem>
                  <SelectItem value="products">Products/Parts</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {uploadFile ? (
                <div>
                  <FileSpreadsheet className="h-12 w-12 mx-auto mb-2 text-green-600" />
                  <p className="font-medium">{uploadFile.name}</p>
                  <p className="text-sm text-gray-500">
                    {(uploadFile.size / 1024).toFixed(1)} KB
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2"
                    onClick={() => setUploadFile(null)}
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <div>
                  <Upload className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                  <p className="text-gray-600">
                    Drag and drop your CSV file here, or
                  </p>
                  <label className="cursor-pointer">
                    <span className="text-blue-600 hover:underline">browse</span>
                    <input
                      type="file"
                      accept=".csv"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </label>
                </div>
              )}
            </div>

            {uploading && (
              <div className="space-y-2">
                <p className="text-sm text-gray-500">Uploading and processing...</p>
                <Progress value={50} className="animate-pulse" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!uploadFile || !uploadType || uploading}
            >
              {uploading ? 'Uploading...' : 'Upload & Process'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exceptions Dialog */}
      <Dialog open={exceptionsDialogOpen} onOpenChange={setExceptionsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Import Details</DialogTitle>
            <DialogDescription>
              Import #{selectedImport?.id} - {exceptions.length} exceptions found
            </DialogDescription>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Row</TableHead>
                <TableHead>Column</TableHead>
                <TableHead>Severity</TableHead>
                <TableHead>Message</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {exceptions.map((ex) => (
                <TableRow key={ex.id}>
                  <TableCell>{ex.row_number}</TableCell>
                  <TableCell>{ex.column_name || '-'}</TableCell>
                  <TableCell>
                    <Badge
                      className={
                        ex.severity === 'error'
                          ? 'bg-red-100 text-red-800'
                          : ex.severity === 'warning'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-blue-100 text-blue-800'
                      }
                    >
                      {ex.severity_display}
                    </Badge>
                  </TableCell>
                  <TableCell>{ex.message}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <DialogFooter>
            <Button onClick={() => setExceptionsDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BulkUpload;
